import { createBrowserRouter, Navigate } from "react-router";
import { AppShell } from "./AppShell";
import { DashboardPage } from "./pages/DashboardPage";
import { POSPage } from "./pages/POSPage";
import { PosV2Page } from "../features/pos-v2/PosV2Page";
import { PurchaseOrderListPage } from "../features/purchase-orders/pages/PurchaseOrderListPage";
import { PurchaseOrderCreatePage } from "../features/purchase-orders/pages/PurchaseOrderCreatePage";
import { PurchaseOrderDetailPage } from "../features/purchase-orders/pages/PurchaseOrderDetailPage";
import { PurchaseOrderEditPage } from "../features/purchase-orders/pages/PurchaseOrderEditPage";
import { SuppliersPage } from "./pages/SuppliersPage";
import { ReportsPage } from "./pages/ReportsPage";
import { ReportsSalesPage } from "./pages/ReportsSalesPage";
import { ReportsProductsPage } from "./pages/ReportsProductsPage";
import { ReportsInventoryPage } from "./pages/ReportsInventoryPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { RequireAuth } from "../routes/guards/RequireAuth";
import { RequireVerifiedBusiness } from "../routes/guards/RequireVerifiedBusiness";
import { RequireRole } from "../routes/guards/RequireRole";
import { RequireNonSuperAdmin } from "../routes/guards/RequireNonSuperAdmin";
import { RequireCompleteBusinessProfile } from "../routes/guards/RequireCompleteBusinessProfile";
import { LoginPage } from "../features/auth/LoginPage";
import { BusinessOnboardingPage } from "../features/business-profile/BusinessOnboardingPage";
import { BusinessProfileViewPage } from "../features/business-profile/BusinessProfileViewPage";
import { BusinessProfileEditPage } from "../features/business-profile/BusinessProfileEditPage";
import { RegisterPage } from "../features/auth/RegisterPage";
import { PendingVerificationPage } from "../features/auth/PendingVerificationPage";
import { BusinessVerificationPage } from "../features/admin/BusinessVerificationPage";
import { AdminDashboardPage } from "../features/admin/AdminDashboardPage";
import { UsersManagementPage } from "../features/admin/UsersManagementPage";
import { ReportsHubPage } from "../features/admin/ReportsHubPage";
import { AuditLogPage } from "../features/admin/AuditLogPage";
import { AdminProfilePage } from "../features/admin/AdminProfilePage";
import { BusinessesManagementPage } from "../features/admin/BusinessesManagementPage";
import { BusinessProfileDetailPage } from "../features/admin/BusinessProfileDetailPage";
import { DealerListPage } from "../features/dealers/pages/DealerListPage";
import { DealerUpsertPage } from "../features/dealers/pages/DealerUpsertPage";
import { ProductListPage } from "../features/products/pages/ProductListPage";
import { ProductCreatePage } from "../features/products/pages/ProductCreatePage";
import { ProductEditPage } from "../features/products/pages/ProductEditPage";
import { InventoryEntriesPage } from "../features/inventory/pages/InventoryEntriesPage";
import { InventoryEntryCreatePage } from "../features/inventory/pages/InventoryEntryCreatePage";
import { InventoryBulkUploadPage } from "../features/inventory/pages/InventoryBulkUploadPage";
import { InvoiceUploadPage } from "../features/inventory/pages/InvoiceUploadPage";
import { CategoryPage } from "../features/products/categories/CategoryPage";
import { BrandsPage } from "../features/products/brands/BrandsPage";
import { RecentActivityPage } from "./pages/RecentActivityPage";
import { AdminRecentActivityPage } from "../features/admin/AdminRecentActivityPage";
import { RouteAnalyticsLayout } from "./RouteAnalyticsLayout";
import { SalesPage } from "../features/sales/pages/SalesPage";
import { PromotionsPage } from "../features/promotions/pages/PromotionsPage";
import { PromotionStackingPage } from "../features/promotions/pages/PromotionStackingPage";

export const router = createBrowserRouter([
  {
    element: <RouteAnalyticsLayout />,
    children: [
  { path: "/login", Component: LoginPage },
  { path: "/register", Component: RegisterPage },
  {
    path: "/pending-verification",
    element: <RequireAuth />,
    children: [{ index: true, Component: PendingVerificationPage }],
  },
  {
    path: "/admin",
    element: <RequireAuth />,
    children: [
      {
        element: <RequireRole roles={["super_admin"]} />,
        children: [
          {
            Component: AppShell,
            children: [
              { index: true, Component: AdminDashboardPage },
              { path: "users", Component: UsersManagementPage },
              { path: "businesses", Component: BusinessesManagementPage },
              { path: "businesses/:businessId/profile", Component: BusinessProfileDetailPage },
              { path: "reports", Component: ReportsHubPage },
              { path: "verifications", Component: BusinessVerificationPage },
              { path: "audit", Component: AuditLogPage },
              { path: "recent-activity", Component: AdminRecentActivityPage },
              { path: "profile", Component: AdminProfilePage },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/",
    element: <RequireAuth />,
    children: [
      {
        element: <RequireVerifiedBusiness />,
        children: [
          { path: "onboarding/business-profile", Component: BusinessOnboardingPage },
          {
            element: <RequireNonSuperAdmin />,
            children: [
              {
                Component: AppShell,
                children: [
                  { index: true, Component: DashboardPage },
                  {
                    element: <RequireCompleteBusinessProfile />,
                    children: [
                      { path: "pos", Component: PosV2Page },
                      { path: "pos-v1", Component: POSPage },
                      { path: "pos-v2", element: <Navigate to="/pos" replace /> },
                      { path: "dealers", Component: DealerListPage },
                      { path: "dealers/new", Component: DealerUpsertPage },
                      { path: "dealers/:id/edit", Component: DealerUpsertPage },
                      { path: "products", Component: ProductListPage },
                      { path: "products/new", Component: ProductCreatePage },
                      { path: "products/:id/edit", Component: ProductEditPage },
                      { path: "recent-activity", Component: RecentActivityPage },
                      { path: "inventory", Component: InventoryEntriesPage },
                      { path: "inventory/new", Component: InventoryEntryCreatePage },
                      { path: "inventory/bulk", Component: InventoryBulkUploadPage },
                      { path: "inventory/invoice", Component: InvoiceUploadPage },
                      { path: "sales", Component: SalesPage },
                      { path: "purchase-orders", Component: PurchaseOrderListPage },
                      { path: "purchase-orders/new", Component: PurchaseOrderCreatePage },
                      { path: "purchase-orders/:id/edit", Component: PurchaseOrderEditPage },
                      { path: "purchase-orders/:id", Component: PurchaseOrderDetailPage },
                      { path: "suppliers", Component: SuppliersPage },
                      { path: "reports", Component: ReportsPage },
                      { path: "reports/sales", Component: ReportsSalesPage },
                      { path: "reports/products", Component: ReportsProductsPage },
                      { path: "reports/inventory", Component: ReportsInventoryPage },
                      { path: "analytics", Component: AnalyticsPage },
                      { path: "settings", Component: SettingsPage },
                      { path: "settings/business-profile", Component: BusinessProfileViewPage },
                      { path: "settings/business-profile/edit", Component: BusinessProfileEditPage },
                      { path: "settings/categories", Component: CategoryPage },
                      { path: "settings/brands", Component: BrandsPage },
                      { path: "settings/promotions", Component: PromotionsPage },
                      { path: "settings/promotions/stacking", Component: PromotionStackingPage },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
    ],
  },
]);
